import { Routes } from '@angular/router';
import { termsRoutes } from './features/check-in/components/legal-acknowledgement-page/terms-routes';
import { LandingPageComponent } from './features/landing-page/landing-page.component';
import { deviceSupportGuard } from './core/guards/device-support.guard';
import { signingSessionAccessGuard } from './core/guards/signing-session-access.guard';
import { checkInResumeGuard } from './core/guards/check-in-resume.guard';
import { sessionCanceledGuard } from './core/guards/session-canceled.guard';
import { msalAuthGuard } from './features/msal-auth/guards/msal-auth.guard';
import { signingAccessibleGuard } from './core/guards/signing-accessible.guard';
import { SUPPRESS_CHAT_BUTTON } from './features/help-faq/components/chat-button-open/chat-button-suppression.service';
import { CheckInFlowStore } from './features/check-in/store/check-in-flow.store';
import { LegalAcknowledgementsStore } from './features/check-in/store/legal-acknowledgements.store';

export const routes: Routes = [
  {
    path: '',
    providers: [CheckInFlowStore, LegalAcknowledgementsStore],
    canActivate: [deviceSupportGuard, msalAuthGuard, signingSessionAccessGuard],
    canActivateChild: [checkInResumeGuard],
    children: [
      {
        path: '',
        component: LandingPageComponent,
        data: {
          pageName: 'Landing Page',
          showExitSigning: true,
          [SUPPRESS_CHAT_BUTTON]: true,
        },
      },
      {
        path: 'id-verification',
        loadComponent: () =>
      import(
        './features/check-in/components/id-verification/id-verification.component'
      ).then((m) => m.IdVerificationComponent),
        data: { pageName: 'ID Verification', showExitSigning: true, [SUPPRESS_CHAT_BUTTON]: true, },
      },
      {
        path: 'personal-information',
        loadComponent: () =>
          import(
            './features/check-in/components/participant-registration/participant-registration.component'
          ).then((m) => m.ParticipantRegistrationComponent),
        data: { pageName: 'Personal Information', showExitSigning: true, [SUPPRESS_CHAT_BUTTON]: true, },
      },
      {
        path: 'mfa',
        loadComponent: () =>
          import(
            './features/check-in/components/mfa/mfa.component'
          ).then((m) => m.MfaComponent),
        data: { pageName: 'MFA Verification', showExitSigning: true, [SUPPRESS_CHAT_BUTTON]: true, },
      },
      {
        path: 'security-questions',
        loadComponent: () =>
          import(
            './features/check-in/components/knowledge-based-auth/knowledge-based-auth.component'
          ).then((m) => m.KnowledgeBasedAuthComponent),
        data: { pageName: 'Security Questions', showExitSigning: true, [SUPPRESS_CHAT_BUTTON]: true, },
      },
      {
        path: 'signature-and-initials',
        loadComponent: () =>
          import(
            './features/signature-and-initials/signature-and-initials.component'
          ).then((m) => m.SignatureAndInitialsComponent),
        data: {
          pageName: 'Signature and Initials Creation',
          showExitSigning: true,
          [SUPPRESS_CHAT_BUTTON]: true,
        },
      },
    ],
  },
  ...termsRoutes,
  {
    path: 'signing-room',
    loadComponent: () =>
      import('./features/signing-room/signing-room.component').then(
        (m) => m.SigningRoomComponent,
      ),
    canActivate: [deviceSupportGuard, msalAuthGuard, signingSessionAccessGuard],
    data: {
      footerType: 'none',
      pageName: 'Document Signing Room',
      preload: true,
      showExitSigning: true,
    },
  },
  {
    path: 'session-inaccessible',
    loadComponent: () =>
      import(
        './features/session-inaccessible/session-inaccessible.component'
      ).then((m) => m.SessionInaccessibleComponent),
    data: { pageName: 'Unable to Access Signing Session', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: 'session-canceled',
    loadComponent: () =>
      import(
        './features/session-canceled/session-canceled.component'
      ).then((m) => m.SessionCanceledComponent),
    canActivate: [sessionCanceledGuard],
    data: { pageName: 'Signing Session Canceled', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: 'not-found',
    loadComponent: () =>
      import('./features/not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
    data: { pageName: 'Not Found', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: 'session-end',
    loadComponent: () =>
      import('./features/session-end/session-end.component').then(
        (m) => m.SessionEndComponent,
      ),
    canActivate: [msalAuthGuard],
    data: { pageName: 'Session End', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: 'waiting-room',
    loadComponent: () =>
      import('./features/waiting-room/waiting-room.component').then(
        (m) => m.WaitingRoomComponent,
      ),
    canActivate: [msalAuthGuard],
    data: { pageName: 'Waiting Room' },
  },
  {
    path: 'exit-signing',
    loadComponent: () =>
      import('./features/exit-signing/exit-signing.component').then(
        (m) => m.ExitSigningComponent,
      ),
    canActivate: [deviceSupportGuard, msalAuthGuard, signingSessionAccessGuard],
    data: { pageName: 'Exit Signing', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: 'join/:joinCode',
    loadComponent: () =>
      import(
        './features/participant-selection/participant-selection.component'
      ).then((m) => m.ParticipantSelectionComponent),
    canActivate: [deviceSupportGuard, signingAccessibleGuard],
    data: { pageName: 'Participant Selection', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: 'unsupported-browser',
    loadComponent: () =>
      import(
        './features/unsupported-browser/unsupported-browser.component'
      ).then((m) => m.UnsupportedBrowserComponent),
    data: { pageName: 'Unsupported Browser', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: 'unsupported-device',
    loadComponent: () =>
      import(
        './features/unsupported-device/unsupported-device.component'
      ).then((m) => m.UnsupportedDeviceComponent),
    data: { pageName: 'Unsupported Device', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: 'unsupported-operating-system',
    loadComponent: () =>
      import(
        './features/unsupported-operating-system/unsupported-operating-system.component'
      ).then((m) => m.UnsupportedOperatingSystemComponent),
    data: { pageName: 'Unsupported Operating System', [SUPPRESS_CHAT_BUTTON]: true, },
  },
  {
    path: '**',
    loadComponent: () =>
      import('./features/not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
    data: { pageName: 'Not Found', [SUPPRESS_CHAT_BUTTON]: true, },
  },
];
